import React from 'react';
import Footer from '../components/Footer';
import LegalShell, { LegalNav, H2 } from '../components/LegalShell';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <LegalNav />
      <main className="container mx-auto max-w-3xl px-6 py-12 md:py-16">
        <LegalShell title="Terms &amp; Conditions" lastUpdated="February 2026">
          <p>
            These Terms &amp; Conditions ("Terms") govern your access to and use of the
            ResetProtocol mobile applications and the website at
            <a href="https://resetprotocol.app" className="text-[#a83a4a] hover:underline mx-1">resetprotocol.app</a>
            (collectively, the "Service"), operated by ResetProtocol ("we", "us", "our"). By
            using the Service, you agree to these Terms. If you do not agree, do not use the
            Service.
          </p>

          <H2>1. Eligibility</H2>
          <p>
            You must be at least 18 years old and legally able to enter into a binding
            contract to use the Service. By creating an account you represent that you meet
            this requirement.
          </p>

          <H2>2. Account &amp; Acceptable Use</H2>
          <p>You agree to:</p>
          <ul className="list-disc pl-6 space-y-2 marker:text-[#a83a4a]/60">
            <li>Provide accurate information and keep your credentials secure.</li>
            <li>Use the Service only for lawful, personal, non-commercial purposes.</li>
            <li>Not reverse-engineer, scrape, or attempt to disrupt the Service.</li>
            <li>Not use the Service to harass, threaten, or harm others.</li>
            <li>Not impersonate another person or misrepresent your identity.</li>
          </ul>
          <p>
            We may suspend or terminate accounts that violate these rules, with or without
            notice.
          </p>

          <H2>3. Subscriptions, Free Trial &amp; Auto-Renewal</H2>
          <p>
            ResetProtocol is offered as a paid auto-renewing subscription, with an optional
            free trial. The following terms apply to all subscriptions purchased through the
            App Store or Google Play:
          </p>
          <ul className="list-disc pl-6 space-y-2 marker:text-[#a83a4a]/60">
            <li>
              <strong>Payment</strong> is charged to your Apple ID or Google Play account at
              confirmation of purchase.
            </li>
            <li>
              <strong>Free trial</strong>: if a free trial is offered, you will not be
              charged during the trial period. If you do not cancel before the trial ends,
              your subscription will automatically convert to a paid subscription at the
              listed price.
            </li>
            <li>
              <strong>Auto-renewal</strong>: subscriptions automatically renew at the end of
              each billing period at the then-current price, unless cancelled at least
              <strong> 24 hours before the end</strong> of the current period.
            </li>
            <li>
              <strong>Managing &amp; cancelling</strong>: subscriptions are managed in your
              device settings. On iOS:
              <em> Settings &gt; [your name] &gt; Subscriptions</em>. On Android:
              <em> Google Play &gt; Profile &gt; Payments &amp; subscriptions &gt; Subscriptions</em>.
              You can cancel any time. Cancellation stops the next renewal but does not
              refund the current period.
            </li>
            <li>
              <strong>Refunds</strong> are handled by Apple or Google under their respective
              policies. We are not able to issue store-side refunds directly.
            </li>
            <li>
              <strong>Unused free trial</strong> portions are forfeited when you purchase a
              subscription, where applicable by law.
            </li>
          </ul>

          <H2>4. No Medical, Therapeutic, or Professional Advice</H2>
          <p>
            ResetProtocol is a self-improvement and wellness tool. It is{' '}
            <strong>not</strong> a substitute for professional medical advice, diagnosis, or
            treatment, and is not a therapy service. The AI Coach is an automated tool, not a
            licensed clinician. Always seek the advice of a qualified health provider with
            any questions about a medical or mental-health condition. If you are in crisis or
            having thoughts of self-harm, contact your local emergency services immediately.
          </p>

          <H2>5. Intellectual Property</H2>
          <p>
            The Service, including its design, code, copy, trademarks, and brand assets, is
            owned by ResetProtocol and protected by copyright and trademark law. You retain
            ownership of the content you create (check-ins, journal entries, etc.). By using
            the Service you grant us a limited license to host and process that content
            solely to operate the Service for you.
          </p>

          <H2>6. Limitation of Liability</H2>
          <p>
            To the maximum extent permitted by law, the Service is provided "as is" and "as
            available" without warranties of any kind. ResetProtocol is not liable for any
            indirect, incidental, special, consequential, or punitive damages, including loss
            of data, profits, or wellbeing, arising from your use of the Service. Our total
            liability for any claim shall not exceed the amount you paid us in the 12 months
            preceding the claim.
          </p>

          <H2>7. Termination</H2>
          <p>
            You can stop using the Service and delete your account at any time. We may
            suspend or terminate your access if you breach these Terms, abuse the Service, or
            if required by law. On termination your data will be erased per our Privacy
            Policy.
          </p>

          <H2>8. Changes to the Service or these Terms</H2>
          <p>
            We may modify the Service, pricing, or these Terms at any time. We will post the
            updated Terms with a new "Last updated" date and, where required by law, notify
            you. Your continued use of the Service after the update constitutes acceptance.
          </p>

          <H2>9. Governing Law &amp; Disputes</H2>
          <p>
            These Terms are governed by the laws of <strong>England and Wales</strong>,
            without regard to conflict-of-laws principles. Any dispute arising out of or
            relating to these Terms or the Service shall be subject to the exclusive
            jurisdiction of the courts of England and Wales, except where mandatory consumer
            protection laws of your country of residence provide otherwise.
          </p>

          <H2>10. Apple-Required Terms (iOS)</H2>
          <p>
            You acknowledge that these Terms are between you and ResetProtocol, not Apple,
            and that Apple is not responsible for the Service or its content. Apple has no
            obligation to furnish maintenance or support. In the event of a failure to
            conform to applicable warranty, you may notify Apple, and Apple will refund the
            purchase price; Apple has no other warranty obligation. Apple and its
            subsidiaries are third-party beneficiaries of these Terms and may enforce them
            against you.
          </p>

          <H2>11. Contact</H2>
          <p>
            For any questions regarding these Terms, contact{' '}
            <a href="mailto:support@resetprotocol.app" className="text-[#a83a4a] hover:underline">support@resetprotocol.app</a>.
          </p>
        </LegalShell>
      </main>
      <Footer />
    </div>
  );
}
